"use client"

import { useEffect, useState, useCallback, useMemo } from "react"
import { codeValueService } from "@/lib/code-value-service"
import type { CodeItem } from "@/types/code-values"

export function useCodeValues() {
  const [isReady, setIsReady] = useState(false)

  useEffect(() => {
    if (typeof window !== "undefined" && window.PhoenixCodeValues) {
      setIsReady(true)
      return
    }

    const interval = setInterval(() => {
      if (typeof window !== "undefined" && window.PhoenixCodeValues) {
        setIsReady(true)
        clearInterval(interval)
      }
    }, 500)

    return () => clearInterval(interval)
  }, [])

  const getCodeValueText = useCallback(
    (id: number, groupName: string): string => codeValueService.getCodeValueText(id, groupName),
    [],
  )

  const getCodeValueCode = useCallback(
    (id: number, groupName: string): string => codeValueService.getCodeValueCode(id, groupName),
    [],
  )

  const getCodeValueIdByCode = useCallback(
    (code: string, groupName: string): number | null => codeValueService.getCodeValueIdByCode(code, groupName),
    [],
  )

  const getCodeValueTextByCode = useCallback(
    (code: string, groupName: string): string => codeValueService.getCodeValueTextByCode(code, groupName),
    [],
  )

  const getCodeValue = useCallback(
    (id: number, groupName: string): CodeItem | null => codeValueService.getCodeValue(id, groupName),
    [],
  )

  const getCodeValueByCode = useCallback(
    (code: string, groupName: string): CodeItem | null => codeValueService.getCodeValueByCode(code, groupName),
    [],
  )

  const getRelatedCodeValues = useCallback(
    (groupName: string, parentId: number, parentGroup: string): CodeItem[] =>
      codeValueService.getCodeValueGroup(groupName).filter(
        (item) => item.parentId === parentId && item.parentGroup === parentGroup,
      ),
    [],
  )

  const getRelatedCodeValuesByCode = useCallback(
    (groupName: string, parentCode: string, parentGroup: string): CodeItem[] =>
      codeValueService.getCodeValueGroup(groupName).filter(
        (item) => item.parentCode === parentCode && item.parentGroup === parentGroup,
      ),
    [],
  )

  const getCodeValues = useCallback(
    (groupName: string, required = true): CodeItem[] => codeValueService.getCodeValues(groupName, required),
    [],
  )

  const getCodeValueGroup = useCallback(
    (groupName: string): CodeItem[] => codeValueService.getCodeValueGroup(groupName),
    [],
  )

  return useMemo(
    () => ({
      isReady,
      getCodeValueText,
      getCodeValueCode,
      getCodeValueIdByCode,
      getCodeValueTextByCode,
      getCodeValue,
      getCodeValueByCode,
      getRelatedCodeValues,
      getRelatedCodeValuesByCode,
      getCodeValues,
      getCodeValueGroup,
    }),
    [
      isReady,
      getCodeValueText,
      getCodeValueCode,
      getCodeValueIdByCode,
      getCodeValueTextByCode,
      getCodeValue,
      getCodeValueByCode,
      getRelatedCodeValues,
      getRelatedCodeValuesByCode,
      getCodeValues,
      getCodeValueGroup,
    ],
  )
}
